import React from 'react';

interface TransactionFeesData {
  transaction_fees_percentage: number;
  payout_amount: number | string;
  transaction_fee_amount: number | string;
  total_amount: number | string;
}

interface TransactionFeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  feesData: TransactionFeesData;
  beneficiaryName: string;
  beneficiaryAccount: string;
}

const TransactionFeesModal: React.FC<TransactionFeesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  feesData,
  beneficiaryName,
  beneficiaryAccount
}) => {
  if (!isOpen) return null;

  const formatCurrency = (amount: number | string) => {
    // Handle NaN or undefined amounts
    const numAmount = parseFloat(amount as string) || parseFloat(amount as any) || 0;
    if (isNaN(numAmount) || numAmount === undefined || numAmount === null) {
      return '₹0.00';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(numAmount);
  };

  // Calculate total amount safely
  const calculateTotal = () => {
    // Try to use total_amount first
    const totalAmount = parseFloat(feesData.total_amount as string) || parseFloat(feesData.total_amount as any);
    if (!isNaN(totalAmount) && totalAmount > 0) {
      return totalAmount;
    }
    
    // Calculate from individual amounts
    const payout = parseFloat(feesData.payout_amount as string) || parseFloat(feesData.payout_amount as any) || 0;
    const fee = parseFloat(feesData.transaction_fee_amount as string) || parseFloat(feesData.transaction_fee_amount as any) || 0;
    const total = payout + fee;
    return total;
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <span className="text-2xl">💳</span>
            <h2 className="text-lg font-bold text-slate-800 m-0">Transaction Fee Confirmation</h2>
          </div>
          <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-100 cursor-pointer text-2xl" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div className="flex flex-col gap-2 mb-5">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-slate-600 text-sm">👤 Beneficiary</span>
              <span className="font-semibold text-slate-800">{beneficiaryName}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-slate-600 text-sm">🏦 Account</span>
              <span className="font-mono text-slate-800 text-sm">{beneficiaryAccount}</span>
            </div>
          </div>

          <div className="rounded-xl border-2 border-slate-200 p-4 mb-5">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider m-0 mb-4">💰 Transaction Breakdown</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-600 text-sm">🎯 Amount to Beneficiary</span>
                <span className="font-bold text-slate-800">{formatCurrency(feesData.payout_amount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 text-sm">📊 Transaction Fee ({feesData.transaction_fees_percentage}%)</span>
                <span className="font-semibold text-amber-600">+{formatCurrency(feesData.transaction_fee_amount)}</span>
              </div>
              <div className="flex items-center gap-2 my-2">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-slate-400 font-bold">=</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <span className="text-slate-700 font-semibold">💳 Total from Your Wallet</span>
                <span className="font-bold text-violet-600 text-lg">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-5">
            <span className="text-2xl flex-shrink-0">✅</span>
            <div>
              <strong className="text-slate-800 text-sm">Beneficiary will receive:</strong>
              <p className="text-lg font-bold text-emerald-700 m-0 mt-1">{formatCurrency(feesData.payout_amount)}</p>
              <p className="text-xs text-slate-600 m-0 mt-1">Full amount! {feesData.transaction_fees_percentage}% fee charged separately from your wallet</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-semibold text-sm cursor-pointer hover:bg-slate-50 flex items-center justify-center gap-2">
              ❌ Cancel
            </button>
            <button type="button" onClick={onConfirm} className="flex-1 py-3 px-4 rounded-xl bg-violet-600 text-white font-semibold text-sm cursor-pointer hover:bg-violet-700 flex items-center justify-center gap-2">
              🚀 Proceed with Payout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionFeesModal;
